// React imports
import React, { useEffect, useState } from 'react';

import { MtxGrid } from '@meilleurtaux/mtp-uikit-react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { ChartResponse, getGetApiPlacementLivretQueryKey, useGetApiPlacementLivret } from '@/api';
import Accordion from '@/components/Accordion/Accordion';
import { accordionItems } from '@/components/Accordion/accordionLivretData';
import Breadcrumb from '@/components/Breadcrumb/Breadcrumb';
import CardGererContrat from '@/components/CardGererContrat/CardGererContrat';
import ChipsRadioSmall from '@/components/chipsRadioSmall/chipsRadioSmall';
import ContractDetailsHeader from '@/components/ContractDetailsHeader/ContractDetailsHeader';
import DemoTag from '@/components/DemoTag/DemoTag';
import LineChart from '@/components/LineChart/LineChart';
import { dataButtonFiltre } from '@/components/LineChart/lineChartButtons';
import { Loader } from '@/components/Loader/loader';
import ModaleDefaut from '@/components/ModaleDefaut/ModaleDefaut';
import Tabs from '@/components/Tabs/Tabs';
import { TrendingValue } from '@/components/TrendingValue/TrendingValue';
import { ROUTE } from '@/constants/route';
import { TIMESPAN } from '@/enums/timespan';
import { useDemoContext } from '@/hooks/DemoContext';
import { useAuth } from '@/hooks/useAuth';
import useCancelModal from '@/hooks/useCancelModal';
import useScreenSize from '@/hooks/useScreenSize';
import { TRACKING_INSTANCE } from '@/hooks/useTracking/useTrackingInit/useTrackingInit';
import ContentModalCalculPerformance from '@/modals/ContentModalCalculPerformance/ContentModalCalculPerformance';
import { PopinLivret } from '@/modals/ContentModalCalculPerformance/data';
import ContentModalRenonciation from '@/modals/ContentModalRenonciation/ContentModalRenonciation';
import { modalTiBoosteContent, modalTiStandardContent } from '@/modals/ContentModalTauxLivret/ContentModalTauxLivret';
import { OperationContrat } from '@/pages/OperationContrat/OperationContrat';
import { BreadcrumbItem } from '@/services/types/Breadcrumb';
import { performanceFilter } from '@/utils/functions/performanceFilter/performanceFilter';

import styles from './styles.module.css';

// Hooks

export const LivretDetails: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isDemo } = useDemoContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { windowWidth } = useScreenSize();
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, isError } = useGetApiPlacementLivret(id ?? '');
  const [lineChartTimespan, setLineChartTimespan] = useState<TIMESPAN>(TIMESPAN.allTime);
  const [showModalCalculPerf, setShowModalCalculPerf] = useState<boolean>(false);
  const [showModalTIStandard, setShowModalTIStandard] = useState<boolean>(false);
  const [showModalTIBooste, setShowModalTIBooste] = useState<boolean>(false);
  const { showCancelModal, openCancelModal, closeCancelModal } = useCancelModal();
  const cancelablePeriodDays = data?.cancelablePeriod == null ? 14 : Number(data.cancelablePeriod);
  const [performance, setPerformance] = useState<number | null>(0);
  const [plusMoinsValue, setPlusMoinsValue] = useState<number | null>(0);

  const { filterPerformanceInvestment } = performanceFilter(data as ChartResponse);

  const openModal = (modal: string) => {
    document.body.classList.add('modal-open');

    switch (modal) {
      case 'showModalCalculPerf':
        setShowModalCalculPerf(true);
        break;

      case 'showModalTIStandard':
        setShowModalTIStandard(true);
        break;

      case 'showModalTIBooste':
        setShowModalTIBooste(true);
        break;

      default:
        break;
    }
  };

  const closeModal = () => {
    document.body.classList.remove('modal-open');
    setShowModalCalculPerf(false);
    setShowModalTIStandard(false);
    setShowModalTIBooste(false);
  };

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: 'Mes contrats', path: ROUTE.CONTRATS },
    { label: 'Livret Meilleurtaux', path: ROUTE.RETRAITE },
  ];

  useEffect(() => {
    if (data) {
      filterPerformanceInvestment({ lineChartTimespan, setPerformance, setPlusMoinsValue });
    }
  }, [data, lineChartTimespan, filterPerformanceInvestment]);

  useEffect(() => {
    if (data) {
      TRACKING_INSTANCE.setTcVars({
        page_nom: `mes contrats - ${data?.famille}`,
        page_template: 'espace client',
        user_id: user?.id,
        user_client: user?.type === 'Client',
        user_connexion: user?.success,
        user_statut_contrat: sessionStorage.getItem('user_statut_contrat'),
      });
      void TRACKING_INSTANCE.triggerEvent('page', undefined, {
        event_id: 'page',
        event_categorie: 'affichage espace client',
        event_nom: `mes contrats - ${data?.famille} - contrat`,
        event_page: `mes contrats - ${data?.famille} - contrat`,
      });
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [data, user]);

  if (isLoading) {
    return (
      <div>
        {' '}
        <Loader />
      </div>
    );
  }

  if (isError) {
    navigate(ROUTE.ERREUR, { state: { goTo: ROUTE.CONTRATS } });
    return null;
  }

  if (!data) {
    return <p>{t('common.noDataText')}</p>;
  }

  const controlInterests =
    (data?.interetsAVenir?.interetsNet ?? 0) >= 0 ? styles.positiveInterestsStyles : styles.negativeInterestsStyles;

  const valorisationEncours = data?.valorisation?.dateEncours
    ? data.valorisation.dateEncours.split('-').reverse().join('/')
    : '--/--/----';

  const subscriptionDate = data?.dateOuverture ? data.dateOuverture.split('-').reverse().join('/') : null;

  return (
    <div className={styles.contractDetailsContainer}>
      {showModalCalculPerf && (
        <ModaleDefaut
          content={ContentModalCalculPerformance({ ...PopinLivret.args })}
          btnLabel="J'ai compris"
          btnVariant="primary"
          btnSize="large"
          onClickBtn={() => closeModal()}
          btnFullwidth={false}
          onClickClose={() => closeModal()}
          mobileStyle="defaut"
          size="large"
        />
      )}
      {showModalTIStandard && (
        <ModaleDefaut
          title={t('modals.savingsAccountStandardRateModal.titleText')}
          content={modalTiStandardContent}
          btnLabel={t('modals.savingsAccountStandardRateModal.btnLabel')}
          btnVariant="primary"
          btnSize="large"
          onClickBtn={() => closeModal()}
          btnFullwidth={false}
          onClickClose={() => closeModal()}
          mobileStyle="stick-to-bottom"
          size="large"
        />
      )}
      {showModalTIBooste && (
        <ModaleDefaut
          title={t('modals.savingsAccountBoostedRateModal.titleText')}
          content={modalTiBoosteContent}
          btnLabel={t('modals.savingsAccountBoostedRateModal.btnLabel')}
          btnVariant="primary"
          btnSize="large"
          onClickBtn={() => closeModal()}
          btnFullwidth={false}
          onClickClose={() => closeModal()}
          mobileStyle="stick-to-bottom"
          size="large"
        />
      )}

      {showCancelModal && (
        <ContentModalRenonciation
          nomProduit={data?.nomProduit ?? ''}
          placementId={data?.placementId ?? ''}
          delayDays={cancelablePeriodDays}
          onClose={closeCancelModal}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: getGetApiPlacementLivretQueryKey(id ?? '') });
            closeCancelModal();
          }}
        />
      )}

      <div>
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className={styles.lifeInsuranceInfo}>
        <ContractDetailsHeader
          nomProduit={data.nomProduit}
          isCancelable={data?.isCancelable}
          cancelablePeriod={cancelablePeriodDays}
          manager={data?.gestionnaire}
          openCancelModal={openCancelModal}
        />

        <div>
          <div className={styles.contractDetailsValuationAmountDate}>
            <div className={styles.savingsAccountValuation}>
              <span className={styles.valuation}>
                {t('format.currencyFlexible', { value: data?.valorisation?.encours ?? 0 })}
              </span>
              <button
                className={styles.contractDetailsValuationInfo}
                onClick={() => {
                  void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
                    event_id: 'clic',
                    event_categorie: 'clic',
                    event_metier: 'espace client placement',
                    event_nom: 'info livret',
                    event_detail_1: 'picto',
                  });
                  openModal('showModalCalculPerf');
                }}
              >
                <i className="totem-icon totem-icon-info" />
              </button>
              {isDemo && <DemoTag />}
            </div>

            <p className={styles.contractDetailsValuationDate}>
              {t('savingsAccount.balanceAsOfSubtitle')} {valorisationEncours}
            </p>
          </div>

          <TrendingValue value={plusMoinsValue} secondaryValue={performance} icon={plusMoinsValue !== null} />

          <div className={styles.savingsDetailInterest}>
            <p>
              intérêt à venir :{' '}
              <span className={controlInterests}>
                {`${(data?.interetsAVenir?.interetsNet ?? 0) >= 0 ? ' + ' : ' - '}`}{' '}
                {t('format.currencyFlexible', { value: Math.abs(data?.interetsAVenir?.interetsNet ?? 0) })}
              </span>
            </p>
          </div>
        </div>

        <div>
          <LineChart timespan={lineChartTimespan} donnees={data.chartData ?? []} />
        </div>

        <div className={styles.filterButtonContent}>
          {dataButtonFiltre.map((value, index, buttonsFiltre) => (
            <ChipsRadioSmall
              key={value.value}
              label={windowWidth < 600 ? value.textMobile : value.text}
              name="lineChartButtons"
              value={value.value}
              defaultCheck={index + 1 === buttonsFiltre.length}
              onClick={() => {
                setLineChartTimespan(value.value);
                filterPerformanceInvestment({ lineChartTimespan: value.value, setPerformance, setPlusMoinsValue });
                void TRACKING_INSTANCE.triggerEvent('clic', undefined, value.tracking);
              }}
            />
          ))}
        </div>
      </div>
      <Tabs
        tabs={[
          {
            label: 'Contrat',
            disabled: false,
            onClick: () => {
              void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
                event_id: 'clic',
                event_categorie: 'clic',
                event_metier: 'espace client placement',
                event_nom: 'contrat',
                event_detail_1: 'onglet',
              });
            },
            content: (
              <div className={styles.contractDetailsBody}>
                <div className={styles.contractDetailsSection}>
                  <div>
                    <h2 className={styles.contractDetailsSectionTitle}>
                      {t('savingsAccount.title')}
                      {isDemo && <DemoTag />}
                    </h2>

                    <div
                      style={{
                        width: 72,
                        height: 25,
                        backgroundImage: `url("${data.gestionnaireLogo}")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: 'contain',
                        backgroundPosition: 'center',
                      }}
                    ></div>
                  </div>

                  <MtxGrid col={7} colXs={12} colSm={12} colMd={12} colLg={7}>
                    <table className={styles.contractDetailsTable}>
                      <tbody className={styles.contractDetailsList}>
                        {data.numContrat !== null && data.numContrat !== undefined && (
                          <tr className={styles.contractDetailsLine}>
                            <td className={styles.contractDetailsTitle}>
                              <p>{t('savingsAccount.label')}</p>
                            </td>
                            <td className={styles.contractDetailsValue}>
                              <p>{data.numContrat}</p>
                            </td>
                          </tr>
                        )}
                        {subscriptionDate && (
                          <tr className={styles.contractDetailsLine}>
                            <td className={styles.contractDetailsTitle}>
                              <p>{t('savingsAccount.openingDateLabel')}</p>{' '}
                            </td>
                            <td className={styles.contractDetailsValue}>
                              <p>{subscriptionDate}</p>
                            </td>
                          </tr>
                        )}
                        {typeof data.tauxInteretStandard === 'number' && (
                          <tr className={styles.contractDetailsLine}>
                            <td className={styles.contractDetailsTitle}>
                              <p>{t('savingsAccount.standardInterestRateLabel')}</p>
                              <button
                                className={styles.contractDetailsMoreInfo}
                                onClick={() => {
                                  void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
                                    event_id: 'clic',
                                    event_categorie: 'clic',
                                    event_metier: 'espace client placement',
                                    event_nom: 'info taux interet standard',
                                    event_detail_1: 'picto',
                                  });
                                  openModal('showModalTIStandard');
                                }}
                              >
                                <i className="totem-icon totem-icon-info" />
                              </button>
                            </td>
                            <td className={styles.contractDetailsValue}>
                              <p>{t('format.percentFlexible', { value: data?.tauxInteretStandard })}</p>
                            </td>
                          </tr>
                        )}
                        {typeof data.tauxInteretBooste === 'number' && (
                          <tr className={styles.contractDetailsLine}>
                            <td className={styles.contractDetailsTitle}>
                              <p>{t('savingsAccount.boostedInterestRateLabel')}</p>{' '}
                              <button
                                className={styles.contractDetailsMoreInfo}
                                onClick={() => {
                                  void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
                                    event_id: 'clic',
                                    event_categorie: 'clic',
                                    event_metier: 'espace client placement',
                                    event_nom: 'info taux interet booste',
                                    event_detail_1: 'picto',
                                  });
                                  openModal('showModalTIBooste');
                                }}
                              >
                                <i className="totem-icon totem-icon-info" />
                              </button>
                            </td>
                            <td className={styles.contractDetailsValue}>
                              <p>
                                {data.tauxInteretBooste
                                  ? t('format.percentFlexible', { value: data.tauxInteretBooste })
                                  : 'xxx'}{' '}
                              </p>
                            </td>
                          </tr>
                        )}
                        {typeof data.versementInitial === 'number' && (
                          <tr className={styles.contractDetailsLine}>
                            <td className={styles.contractDetailsTitle}>
                              {' '}
                              <p>{t('savingsAccount.initialDepositLabel')}</p>
                            </td>
                            <td className={styles.contractDetailsValue}>
                              <p>{t('format.currencyFlexible', { value: data.versementInitial })}</p>
                            </td>
                          </tr>
                        )}

                        {typeof data.montantTotalVerse === 'number' && (
                          <tr className={styles.contractDetailsLine}>
                            <td className={styles.contractDetailsTitle}>
                              <p>{t('savingsAccount.totalPaidLabel')}</p>
                            </td>
                            <td className={styles.contractDetailsValue}>
                              <p>
                                {' '}
                                {data.montantTotalVerse === null
                                  ? 'xxx'
                                  : t('format.currencyFlexible', { value: data.montantTotalVerse })}{' '}
                              </p>
                            </td>
                          </tr>
                        )}
                        {typeof data.montantTotalRetire === 'number' && (
                          <tr className={styles.contractDetailsLine}>
                            <td className={styles.contractDetailsTitle}>
                              <p>{t('savingsAccount.totalRedeemedLabel')}</p>{' '}
                            </td>
                            <td className={styles.contractDetailsValue}>
                              <p>
                                {data.montantTotalRetire === null
                                  ? 'xxx'
                                  : t('format.currencyFlexible', { value: data.montantTotalRetire })}{' '}
                              </p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </MtxGrid>
                </div>

                <div className={styles.operationTerms}>
                  <h2>{t('savingsAccount.subtitle')}</h2>

                  <MtxGrid row>
                    <MtxGrid col={4} colXs={12} colSm={12} colMd={12} colLg={7}>
                      <div className={styles.termsAccordion}>
                        <Accordion items={accordionItems} />
                      </div>
                    </MtxGrid>
                  </MtxGrid>
                </div>
                <div className={styles.contractDetailsSectionCard}>
                  <h2>{t('savingsAccount.manageContractSubtitle')}</h2>

                  <MtxGrid row>
                    <MtxGrid col={4} colXs={12} colSm={6} colMd={6} colLg={4}>
                      <CardGererContrat
                        insuranceLink={data.gestionnaireAC ?? ''}
                        insuranceLogo={data.gestionnaireLogo}
                        contractName={data.nomProduit}
                        isLogo
                        isAsideInfo={false}
                      />
                    </MtxGrid>
                  </MtxGrid>
                </div>
              </div>
            ),
          },
          {
            label: 'Mouvements',
            disabled: data?.dernieresOperations?.length === 0,
            onClick: () => {
              void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
                event_id: 'clic',
                event_categorie: 'clic',
                event_metier: 'espace client placement',
                event_nom: 'mouvements',
                event_detail_1: 'onglet',
              });
            },
            content: <OperationContrat data={data?.dernieresOperations} placementId={data?.placementId} />,
          },
        ]}
      />
    </div>
  );
};
